import { useCallback, useEffect, useState } from "react";
import { Check, X, ShieldAlert, Power, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { api, errMsg, num, rupee, pnlClass, istDateTime, ipMatches } from "@/lib/api";
import { Stat } from "@/components/Stat";
import { ConfirmSwitch } from "@/components/ConfirmSwitch";
import { TradesTable, AuditTable } from "@/components/LiveTables";
import { DryRunPanel } from "@/components/DryRunPanel";
import { LiveStrategiesPanel } from "@/components/LiveStrategiesPanel";
import { WeeklyScorecard } from "@/components/WeeklyScorecard";
import { MarginReadiness } from "@/components/MarginReadiness";
import { CapitalPlanner } from "@/components/CapitalPlanner";
import { PnlCalendar } from "@/components/PnlCalendar";
import { AutopilotPanel } from "@/components/AutopilotPanel";
import { MonthlyStatement } from "@/components/MonthlyStatement";

const STATE = {
  locked: ["LOCKED", "border-slate-600 text-slate-400"],
  ready: ["READY · DISARMED", "border-amber-500/40 text-amber-400"],
  armed: ["ARMED · LIVE ORDERS ON", "border-red-500/50 bg-red-500/10 text-red-400"],
  killed: ["KILL SWITCH ENGAGED", "border-red-500/50 bg-red-500/15 text-red-400"],
};

export default function ExecutionTab({ reloadStatus }) {
  const [st, setSt] = useState(null);
  const [trades, setTrades] = useState([]);
  const [audit, setAudit] = useState([]);
  const [ip, setIp] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rev, setRev] = useState(0);

  const load = useCallback(() => {
    api.get("/execution/status").then((r) => setSt(r.data)).catch(() => {});
    api.get("/execution/trades").then((r) => setTrades(r.data)).catch(() => {});
    api.get("/execution/audit").then((r) => setAudit(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); const id = setInterval(load, 10000); return () => clearInterval(id); }, [load]);
  const checkIp = useCallback(() => {
    setIp(null);
    api.post("/execution/proxy-check").catch(() => {});
    api.get("/execution/ip").then((r) => setIp(r.data)).catch((e) => setIp({ error: errMsg(e) }));
  }, []);
  useEffect(() => { checkIp(); }, [checkIp]);

  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); load(); reloadStatus(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const overrideOn = () => act(() => api.post("/execution/override", { phrase: st.override.phrase }), "Validation override ON");
  const overrideOff = () => act(() => api.post("/execution/override/off"), "Validation override OFF");
  const arm = () => act(() => api.post("/execution/arm", { phrase: st.phrase }), "Live trading ARMED for today");
  const disarm = () => act(() => api.post("/execution/disarm"), "Disarmed");
  const kill = () => act(() => api.post("/execution/kill", {}), "Kill switch engaged: positions exited");
  const resetKill = () => act(() => api.post("/execution/reset-kill", { phrase: st.phrase }), "Kill switch reset");
  const reconcile = () => act(() => api.post("/execution/reconcile"), "Reconciliation complete");
  const exitTrade = (id) => act(() => api.post(`/execution/trades/${id}/exit`), "Exit orders filled");

  if (!st) return null;
  const [label, cls] = STATE[st.live_state];
  const open = trades.filter((t) => ["open", "entering", "exiting"].includes(t.status));
  const closed = trades.filter((t) => !["open", "entering", "exiting"].includes(t.status));

  return (
    <div className="grid grid-cols-12 gap-3 sm:gap-4 fade-in">
      <div className="panel col-span-12 p-4 lg:col-span-5" data-testid="panel-live-gates">
        <div className="flex items-center justify-between">
          <span className="eyebrow">Live trading gates</span>
          <span data-testid="live-state-badge" className={`rounded border px-2 py-0.5 text-[10px] font-semibold tracking-wider ${cls}`}>{label}</span>
        </div>
        <ul className="mt-3 space-y-1.5">
          {st.gates.map((g) => (
            <li key={g.key} data-testid={`gate-${g.key}`} className="flex flex-wrap items-start gap-x-2 gap-y-0.5 text-xs sm:flex-nowrap">
              <span className={`mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded ${g.overridden ? "bg-amber-500/20 text-amber-400" : g.ok ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"}`}>
                {g.ok ? <Check size={11} /> : <X size={11} />}
              </span>
              <span className="min-w-0 flex-1 text-slate-300">{g.label}</span>
              <span className="num w-full break-all pl-6 text-slate-500 sm:w-auto sm:max-w-[45%] sm:shrink-0 sm:truncate sm:pl-2 sm:text-right" title={String(g.detail ?? "")}>{typeof g.detail === "object" && g.detail !== null ? "mismatch" : String(g.detail ?? "—")}</span>
            </li>
          ))}
        </ul>
        <IpPanel ip={ip} onCheck={checkIp} />
      </div>

      <div className="col-span-12 flex flex-col gap-3 lg:col-span-7">
        <div className={`panel p-4 ${st.override.on ? "border-amber-500/50" : ""}`} data-testid="panel-validation-override">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="eyebrow">Validation override</span>
            <ConfirmSwitch id="switch-override" tone="amber" checked={st.override.on} disabled={busy}
              label={st.override.on ? `ON since ${istDateTime(st.override.at)}` : "OFF"}
              onOn={overrideOn} onOff={overrideOff} confirmLabel="Override validation gates"
              title="Bypass the validation gates?"
              body="For ALL enabled strategies, the 4 backtest and paper gates will count as passed even when they fail, so strategies that have been losing will trade real money. IP, proxy, kill switch, margin check, max 2 open, 1 lot, ₹1,000 risk (₹2,000 for straddles/strangles) and the ₹4,000 daily stop stay enforced. Stays on until you switch it off. Logged in the audit trail." />
          </div>
          <p className={`mt-2 text-xs ${st.override.on ? "text-amber-300/90" : "text-slate-400"}`} data-testid="override-state">
            {st.override.on ? "Backtest and paper gates are bypassed. All other safeguards still apply."
              : "One switch for every strategy: bypasses only the 4 validation gates (200 OOS trades, OOS PF ≥ 2, 40 paper trades, paper PF ≥ 2). Without it, strategies that fail are skipped live. Expect losses."}
          </p>
        </div>

        <div className={`panel p-4 ${st.armed ? "border-red-500/50" : ""}`} data-testid="panel-arm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="eyebrow">Live auto-trading (daily)</span>
            {st.state.killed ? (
              <ConfirmSwitch id="switch-reset-kill" tone="amber" checked={false} disabled={busy} label="Kill switch engaged · reset"
                onOn={resetKill} onOff={() => {}} confirmLabel="Reset kill switch" title="Reset the kill switch?"
                body="Clears the emergency stop so live trading can be armed again. It does not arm by itself." />
            ) : (
              <ConfirmSwitch id="switch-arm-live" checked={st.armed} disabled={busy || (!st.armed && !st.all_ok)}
                label={st.armed ? "ARMED · real orders on" : st.all_ok ? "Disarmed" : "Locked"}
                onOn={arm} onOff={disarm} confirmLabel="Arm live trading"
                title="Arm live auto-trading for today?"
                body={`The bot will place REAL Dhan orders today on the signals of every enabled strategy (${(st.strategies || []).map((x) => x.label).join(", ")}): 1 lot each, ₹${num(st.hard_risk, 0)} max risk per trade, at most 2 open, hard ₹${num(st.hard_daily_loss, 0)} combined daily stop. Expires at midnight; switch off any time.`} />
            )}
          </div>
          <p className="mt-2 text-xs text-slate-400">
            When armed, the bot places real NSE F&amp;O limit orders on every enabled strategy's signal: bought legs first, then sold legs, chasing {"&"} retrying, then cancelling.
            Fixed at 1 lot, ₹{num(st.hard_risk, 0)} max risk per trade and a hard ₹{num(st.hard_daily_loss, 0)} daily stop. Arming expires at midnight.
          </p>
          {!st.all_ok && !st.armed && <p className="mt-2 text-[11px] text-amber-400" data-testid="arm-blocked-note">Blocked until every gate on the left passes.</p>}
        </div>

        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <Stat label="Live P&L today" value={rupee(st.day_pnl)} cls={pnlClass(st.day_pnl)} sub={`hard stop −₹${num(st.limit, 0)}`} id="stat-live-day-pnl" />
          <Stat label="Open live" value={open.length} id="stat-live-open" />
          <Stat label="Reconciled" value={st.state.reconcile_ok ? "OK" : "MISMATCH"} cls={st.state.reconcile_ok ? "up" : "down"} sub={istDateTime(st.state.reconciled_at)} id="stat-reconcile" />
          <div className="panel flex flex-col justify-center gap-2 p-3">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button data-testid="button-kill-switch" disabled={busy} className="h-10 gap-2 bg-red-700 font-semibold text-white hover:bg-red-600"><Power size={14} /> KILL SWITCH</Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="z-[60] border-red-500/40 bg-[#121826] text-slate-100 opacity-100">
                <AlertDialogHeader>
                  <AlertDialogTitle className="flex items-center gap-2 text-red-400"><ShieldAlert size={18} /> Emergency stop</AlertDialogTitle>
                  <AlertDialogDescription className="text-slate-300">
                    This cancels pending F&amp;O orders, exits every bot position (short leg first) with aggressive limit orders, disarms live trading and, if enabled, activates Dhan's account kill switch for the day.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="button-kill-cancel">Cancel</AlertDialogCancel>
                  <AlertDialogAction data-testid="button-kill-confirm" onClick={kill} className="bg-red-600 hover:bg-red-500">Exit everything now</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <Button data-testid="button-reconcile" size="sm" variant="ghost" onClick={reconcile} className="h-6 gap-1 text-[11px] text-slate-400"><RefreshCw size={11} /> Reconcile now</Button>
          </div>
        </div>
      </div>

      <AutopilotPanel onChange={() => { setRev((r) => r + 1); load(); }} />
      <LiveStrategiesPanel override={st.override.on} onChange={load} rev={rev} />
      <MarginReadiness />
      <CapitalPlanner onChange={() => { setRev((r) => r + 1); load(); }} />
      <WeeklyScorecard onChange={() => { setRev((r) => r + 1); load(); }} />
      <PnlCalendar rev={trades.length} />
      <MonthlyStatement rev={trades.length} />
      <TradesTable title="Live positions" rows={open} id="table-live-open" onExit={exitTrade} busy={busy} />
      <TradesTable title="Live trade history" rows={closed} id="table-live-history" />

      <AuditTable rows={audit} id="panel-audit-log" title="Audit log · every order decision" />
      <DryRunPanel />
    </div>
  );
}

const IpPanel = ({ ip, onCheck }) => {
  const match = ipMatches(ip);
  return (
    <div data-testid="egress-ip" className={`num mt-4 rounded border p-3 text-[11px] ${match ? "border-emerald-500/30 bg-emerald-500/5" : "border-red-500/30 bg-red-500/5"}`}>
      <div className="flex items-center justify-between">
        <span className="eyebrow !text-[10px]">Dhan static IP check (from Dhan)</span>
        <button data-testid="button-recheck-ip" onClick={onCheck} className="text-[11px] text-indigo-300 hover:text-indigo-200">Re-check</button>
      </div>
      {!ip ? <p className="mt-2 text-slate-500">Checking…</p> : ip.error ? <p className="mt-2 text-red-400">{ip.error}</p> : (
        <>
          <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-slate-400">
            <span>Registered primary</span><b className="text-right text-slate-100" data-testid="ip-primary">{ip.primaryIP || "—"}</b>
            <span>Registered secondary</span><b className="text-right text-slate-100" data-testid="ip-secondary">{ip.secondaryIP || "—"}</b>
            <span>Dhan sees this server as</span><b className={`text-right ${match ? "up" : "down"}`} data-testid="ip-detected">{ip.detectedIP || "—"}</b>
            <span>Match · orders allowed</span><b className={`text-right ${match ? "up" : "down"}`} data-testid="ip-match">{ip.ipMatchStatus?.replace("_", " ")} · {ip.ordersAllowed ? "YES" : "NO"}</b>
          </div>
          {!match && (
            <p className="mt-2 leading-snug text-amber-300/90" data-testid="ip-mismatch-help">
              Orders from this server are rejected (DH-905). Fix it in Settings → Dhan proxy: run deploy/dhan-proxy-setup.sh on {ip.primaryIP || "your registered VPS"} and paste the proxy URL it prints.
              This hosted preview's outgoing IP changes over time, so it can't be whitelisted.
            </p>
          )}
        </>
      )}
    </div>
  );
};
